import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Aviso,
  BotaoLink,
  Carregando,
  Estatistica,
  Etiqueta,
  Selo,
  Vazio,
} from '../../componentes/ui/index.ts';
import { api, moeda } from '../../lib/api.ts';
import type { LinhaInscricao, ResumoFinanceiro, Usuario } from '../../lib/tipos.ts';

export function Visao() {
  const [inscricoes, setInscricoes] = useState<LinhaInscricao[]>([]);
  const [resumo, setResumo] = useState<ResumoFinanceiro | null>(null);
  const [pendentes, setPendentes] = useState<Usuario[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    Promise.all([
      api<{ inscricoes: LinhaInscricao[] }>('/api/admin/inscricoes'),
      api<ResumoFinanceiro>('/api/financeiro/resumo'),
      api<{ usuarios: Usuario[] }>('/api/usuarios?status=pendente'),
    ])
      .then(([i, r, u]) => {
        setInscricoes(i.inscricoes);
        setResumo(r);
        setPendentes(u.usuarios);
      })
      .finally(() => setCarregando(false));
  }, []);

  if (carregando) return <Carregando />;

  const semSaude = inscricoes.filter((i) => !i.temSaude).length;
  const semAutorizacao = inscricoes.filter((i) => !i.autorizacaoAssinadaEm).length;

  return (
    <div className="pilha-6">
      <div className="pilha-3">
        <Etiqueta>Visão geral</Etiqueta>
        <h2>Acampamento</h2>
      </div>

      {/* Fila de aprovação em destaque: é a ação que trava outras pessoas. */}
      {pendentes.length > 0 && (
        <Aviso tipo="alerta">
          <strong>
            {pendentes.length} {pendentes.length === 1 ? 'pessoa aguarda' : 'pessoas aguardam'}{' '}
            aprovação de acesso.
          </strong>
          <div style={{ marginTop: 'var(--gap-3)' }}>
            <BotaoLink para="/painel/usuarios" tamanho="pequeno">
              Revisar acessos
            </BotaoLink>
          </div>
        </Aviso>
      )}

      <div className="grade">
        <Estatistica titulo="Inscritos" valor={String(inscricoes.length)} tom="laranja" />
        {/* Esta tela é só de admin, então a API sempre traz os totais aqui —
            o `?? '0'` é só para o TypeScript, que não sabe disso. */}
        {resumo?.recebido !== undefined && (
          <Estatistica titulo="Recebido" valor={moeda(resumo.recebido)} />
        )}
        {resumo?.aReceber !== undefined && (
          <Estatistica
            titulo="A receber"
            valor={moeda(resumo.aReceber)}
            tom={Number(resumo.aReceber) > 0 ? 'alerta' : 'sucesso'}
          />
        )}
        <Estatistica
          titulo="Sem saúde"
          valor={String(semSaude)}
          tom={semSaude > 0 ? 'alerta' : 'sucesso'}
          detalhe={semSaude > 0 ? 'não dá para viajar assim' : 'todas em dia'}
        />
        <Estatistica
          titulo="Sem autorização"
          valor={String(semAutorizacao)}
          tom={semAutorizacao > 0 ? 'alerta' : 'sucesso'}
        />
      </div>

      <section className="pilha">
        <div className="linha linha-entre">
          <h3>Últimas inscrições</h3>
          <BotaoLink para="/painel/inscricoes" variante="fantasma" tamanho="pequeno">
            Ver todas
          </BotaoLink>
        </div>

        {inscricoes.length === 0 ? (
          <Vazio titulo="Nenhuma inscrição ainda">
            Assim que o primeiro responsável enviar uma ficha, ela aparece aqui.
          </Vazio>
        ) : (
          <div className="tabela-caixa">
            <table className="tabela">
              <thead>
                <tr>
                  <th>Protocolo</th>
                  <th>Adolescente</th>
                  <th>Responsável</th>
                  <th>Situação</th>
                </tr>
              </thead>
              <tbody>
                {inscricoes.slice(0, 8).map((i) => (
                  <tr key={i.id}>
                    <td className="mono">{i.protocolo}</td>
                    <td>
                      <Link to={`/painel/inscricoes/${i.id}`} style={{ fontWeight: 600 }}>
                        {i.nomeAdolescente}
                      </Link>
                    </td>
                    <td className="texto-fraco">{i.nomeResponsavel ?? '—'}</td>
                    <td>
                      <span className="linha" style={{ gap: 'var(--gap-1)' }}>
                        {!i.temSaude && <Selo tom="alerta">sem saúde</Selo>}
                        {!i.autorizacaoAssinadaEm && <Selo tom="neutro">sem autorização</Selo>}
                        {i.temSaude && i.autorizacaoAssinadaEm && (
                          <Selo tom="sucesso" ponto>
                            em dia
                          </Selo>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
